import { NotificationImportant, Report } from '@mui/icons-material';
import { Tab, Tabs } from '@mui/material';
import { styled } from '@mui/material/styles';
import { FC, useState } from 'react';
import Container from 'src/components/atoms/Container';

import { StorefrontTypeSelect } from 'src/components/molecules/StorefrontTypeSelect/StorefrontTypeSelect';
import { ScopeEnum } from 'src/domain';
import { useScope } from 'src/hooks';
import { useStyleWarningsRows } from 'src/hooks/style-warnings/table/useStyleWarningsRows';
import { TabPanel } from './components/TabPanel';
import { predictedUnsuccessfulPhaseOutCols, unsuccessfulPhaseOutCols } from './data/columns';

function a11yProps(index: number) {
    return {
        id: `tab-${index}`,
        'aria-controls': `tabpanel-${index}`,
    };
}

export const StyleWarnings: FC = () => {
    const { data: overviewRows, loading } = useStyleWarningsRows();
    const [tabIndex, setTabIndex] = useState(0);
    const scope = useScope();

    return (
        <Wrapper>
            <Container style={{ marginBottom: '1rem' }}>
                {scope !== ScopeEnum.STORE && <StorefrontTypeSelect />}
            </Container>
            <TabsWrapper value={tabIndex} onChange={(_, index) => setTabIndex(index)}>
                <StyledTab
                    icon={<NotificationImportant color="warning" />}
                    iconPosition="start"
                    label="Predicted Unsuccessful Phase Out"
                    {...a11yProps(0)}
                />
                <StyledTab
                    icon={<Report color="error" />}
                    iconPosition="start"
                    label="Unsuccessful Phase Out"
                    {...a11yProps(0)}
                />
            </TabsWrapper>
            {tabIndex === 0 && (
                <TabPanel
                    columns={predictedUnsuccessfulPhaseOutCols}
                    data={overviewRows.predictedUnsuccessfulPhaseOut}
                    loading={!!loading}
                    description={
                        'These style options have a set exit week that they are predicted to miss. This could have to do with either a too high norm or a too low predicted sales quantity leading up to the exit week.'
                    }
                />
            )}
            {tabIndex === 1 && (
                <TabPanel
                    columns={unsuccessfulPhaseOutCols}
                    data={overviewRows.unsuccessfulPhaseOut}
                    loading={!!loading}
                    description={
                        'These style options still have a stock quantity after their exit week. Styles are expected to be at 0 in stock after their exit week.'
                    }
                />
            )}
        </Wrapper>
    );
};

const TabsWrapper = styled(Tabs)(({ theme }) => ({
    zIndex: 2,
    marginTop: theme.spacing(0.5),
    margin: 0,
    height: 40,
    minHeight: 40,
    textTransform: 'initial',
    '& .MuiTabs-indicator': {
        display: 'none',
    },
    '& .Mui-selected': {
        color: theme.palette.text.primary,
        '& + .MuiTabs-root': {
            zIndex: 1,
        },
        '& + .MuiTabs-root:before': {
            opacity: 0,
        },
    },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    minHeight: 40,
    height: 40,
    gap: 10,
    alignItems: 'center',
    opacity: 1,
    overflow: 'initial',
    padding: theme.spacing(1, 5),
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.common.white,
    transition: '0.2s',
    [theme.breakpoints.up('md')]: {
        minWidth: 120,
    },
    '& .MuiTab-icon': {
        margin: 0,
    },
    '&:before': {
        transition: '0.2s',
    },
    '& + .Mui-selected:before': {
        opacity: 0,
    },
    '&:not(.Mui-selected)': {
        backgroundColor: theme.palette.grey[200], // matching body background color
    },
    '&:hover': {
        '&:not(.Mui-selected)': {
            backgroundColor: theme.palette.grey[100],
        },
        '&::before': {
            opacity: 0,
        },
        '& + .MuiTabs-root:before': {
            opacity: 0,
        },
    },
}));

const Wrapper = styled('div')`
  display: flex;
  position: relative;
  flex-direction: column;
  min-height: 0;
  height: 100%;
`;
